import { useState, FormEvent } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch("https://readdy.ai/api/form/d7e2d9fsch5bvrijfq90", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail("");
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#111111] py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="https://storage.readdy-site.link/project_files/ae39c9fb-c0ef-4d19-8b6a-d8adfd2c0a75/32693301-2181-40e9-bc79-b81cbb3228bc_n17thelaneclearWHITE.png?v=e21d2ac9cf8100cfbee6280d62f5a398"
              alt="N17 The Lane Logo"
              className="h-14 w-auto object-contain mb-5"
            />
            <p className="text-white/40 text-sm leading-relaxed">
              The ultimate destination to watch every World Cup 2026 match. Book your table, grab The Full 90 deal, and be part of history.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">
              <a href="#" className="hover:text-[#e8c34a] transition-colors">Quick Links</a>
            </h4>
            <ul className="flex flex-col gap-3">
              {[
                { label: "Match Countdown", href: "#countdown" },
                { label: "Team Highlights", href: "#teams" },
                { label: "Ticket Packages", href: "#tickets" },
                { label: "The Full 90 Deal", href: "#full90" },
                { label: "Game Schedule", href: "#schedule" },
                { label: "Multimedia Highlights", href: "#highlights" },
              ].map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/40 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">
              <a href="#" className="hover:text-[#e8c34a] transition-colors">Follow FIFA 2026</a>
            </h4>
            <ul className="flex flex-col gap-3">
              {[
                { icon: "ri-instagram-line", label: "Instagram", href: "https://www.instagram.com/n17thelane" },
                { icon: "ri-facebook-circle-line", label: "Facebook", href: "https://www.facebook.com/n17thelane/" },
                { icon: "ri-tiktok-line", label: "TikTok", href: "https://www.tiktok.com/@n17thelane" },
              ].map((social) => (
                <li key={social.label}>
                  <a
                    href={social.href}
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-3 text-white/40 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    <i className={`${social.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                    {social.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-2">
              Stay in the Game
            </h4>
            <p className="text-white/40 text-sm mb-5 leading-relaxed">
              Get match alerts, deal drops, and exclusive World Cup 2026 updates straight to your inbox.
            </p>
            {submitted ? (
              <div className="flex items-center gap-2 text-[#e8c34a] text-sm font-medium">
                <i className="ri-check-double-line text-base w-4 h-4 flex items-center justify-center"></i>
                You&apos;re in! Get ready for kickoff.
              </div>
            ) : (
              <form
                data-readdy-form
                id="newsletter-form"
                onSubmit={handleSubmit}
                className="flex items-center gap-2"
              >
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#e8c34a]/50 min-w-0"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-10 h-10 flex items-center justify-center bg-[#e8c34a] rounded-lg hover:bg-[#f0d060] transition-colors cursor-pointer flex-shrink-0"
                >
                  {loading ? (
                    <i className="ri-loader-4-line text-black animate-spin"></i>
                  ) : (
                    <i className="ri-arrow-right-line text-black font-bold"></i>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/20 text-xs tracking-wide">
            &copy; 2026 FIFA World Cup Fan Experience. All rights reserved.
          </p>
          <p className="text-white/20 text-xs tracking-wide">
            Table reservations powered by Resy &middot; Drink responsibly
          </p>
        </div>

        {/* Big watermark */}
        <div className="mt-4 overflow-hidden">
          <p className="text-white/[0.035] font-black text-[80px] md:text-[120px] uppercase leading-none tracking-tighter select-none whitespace-nowrap">
            WORLD CUP 2026
          </p>
        </div>
      </div>
    </footer>
  );
}
